import {
  formatProgressStepLabel,
  type OperationProgressModel,
} from '../../../shared/ui/progress';
import type { MappingProfileApplyProgressState } from '../hooks/profileControllerShared';
import { buildPlannedSteps } from './profile-apply-progress/stepProjection';
import {
  EtaPendingLabel,
  buildProfileApplyDetail,
  buildProfileApplyMessage,
  buildProfileApplyMetrics,
  buildProfileApplyStatus,
  buildStagePercent,
  capRunningStagePercent,
  getCurrentStepIndex,
  hasLiveRevitCounts,
} from './profile-apply-progress/presentation';

export { stabilizeProfileApplyWindowModel } from './profile-apply-progress/modelStabilizer';

export function buildProfileApplyWindowModel(input: {
  operationId?: string;
  profileApplyProgress: MappingProfileApplyProgressState | null;
  readinessProgressModel: OperationProgressModel | null;
  readinessStatus: string;
  readinessMessage: string | null;
  resolvedProfileName: string;
  profileSessionPending?: boolean;
}): OperationProgressModel | null {
  const {
    profileApplyProgress,
    readinessProgressModel,
    readinessStatus,
    readinessMessage,
    resolvedProfileName,
    operationId,
    profileSessionPending = false,
  } = input;

  if (!profileApplyProgress && !readinessProgressModel) {
    return null;
  }

  if (
    profileApplyProgress?.phase === 'complete' &&
    readinessStatus !== 'checking' &&
    readinessStatus !== 'repairing' &&
    readinessStatus !== 'blocked' &&
    readinessStatus !== 'error' &&
    readinessStatus !== 'ready' &&
    !profileSessionPending
  ) {
    return null;
  }

  const steps = buildPlannedSteps({
    profileApplyProgress,
    readinessProgressModel,
    readinessStatus,
    resolvedProfileName,
    profileSessionPending,
  });
  const status = buildProfileApplyStatus({
    profileApplyProgress,
    readinessProgressModel,
    readinessStatus,
    profileSessionPending,
  });
  const childStatusCompleteDuringRepair =
    readinessProgressModel?.status === 'complete' && readinessStatus === 'repairing';
  const liveCounts = hasLiveRevitCounts(readinessProgressModel);
  const currentStepIndex = getCurrentStepIndex(steps);
  const progressOperationId = readinessProgressModel?.operationId
    ? `${operationId ?? 'mapping-profile-apply'}:${readinessProgressModel.operationId}`
    : operationId;

  return {
    ...readinessProgressModel,
    operationId: progressOperationId,
    title: `Applying “${resolvedProfileName}” to the active Revit project`,
    message: buildProfileApplyMessage({
      profileApplyProgress,
      readinessProgressModel,
      readinessStatus,
      readinessMessage,
      profileSessionPending,
    }),
    detail: buildProfileApplyDetail({
      profileApplyProgress,
      readinessProgressModel,
      readinessStatus,
    }),
    status: childStatusCompleteDuringRepair ? 'running' : status,
    state: childStatusCompleteDuringRepair ? 'running' : readinessProgressModel?.state,
    resultReady: childStatusCompleteDuringRepair ? false : readinessProgressModel?.resultReady,
    percent:
      status === 'complete'
        ? 100
        : capRunningStagePercent(status, buildStagePercent(steps, readinessProgressModel)),
    stepLabel: formatProgressStepLabel(currentStepIndex + 1, steps.length, 1),
    etaLabel: liveCounts ? readinessProgressModel?.etaLabel : EtaPendingLabel,
    error:
      profileApplyProgress?.phase === 'error'
        ? profileApplyProgress.error
        : readinessProgressModel?.error ?? (status === 'error' ? readinessMessage ?? undefined : undefined),
    steps,
    metrics: buildProfileApplyMetrics({
      steps,
      readinessProgressModel,
      readinessStatus,
      readinessMessage,
      profileSessionPending,
    }),
    locked: status !== 'error',
  };
}
